import { getLocalTimeZone, today } from '@internationalized/date'
import { addMonths, differenceInCalendarMonths, format, parseISO } from 'date-fns'
import Decimal from 'decimal.js'

import type { TSimulacaoGrupoMensalPrice } from '@/features/simulador/brasil/domain/schemas'
import type { TSimulacaoGrupoMensaisBase } from '@/features/simulador/brasil/types/simulacao.type'
import { CalculadoraJuros } from '@/features/simulador/shared/domain/calculadora-juros'
import { roundFloat } from '@/utils/number.utils'

export class GerenciadorMensaisPrice {
  criarGrupoInicial(
    valor: number,
    valorBase: number,
    dataInicio = today(getLocalTimeZone()).toString(),
    quantidade = 0,
    percentual = 0,
  ) {
    if (!quantidade) return undefined

    const valorTotal = new Decimal(percentual).div(100).mul(valorBase).toNumber()
    const dataFim = format(addMonths(parseISO(dataInicio), quantidade - 1), 'yyyy-MM-dd')

    return { percentual, valorTotal, parcelas: [{ valor, quantidade, dataInicio, dataFim }] }
  }

  private calcularPrestacaoComJuros(
    saldoMensaisSemJuros: number,
    quantidade: number,
    mesesAtePagMensais: number,
    percentualJurosAnualPrice: number,
  ): number {
    const calculadoraJuros = new CalculadoraJuros(percentualJurosAnualPrice)
    const taxaJurosMensal = calculadoraJuros.conversaoTaxaAnualPara('Mensal')
    const valorMensalSemJuros = roundFloat(new Decimal(saldoMensaisSemJuros).div(quantidade).toNumber(), 2)

    const saldoMensaisComJuros = calculadoraJuros.calcularValorFuturo(
      mesesAtePagMensais,
      taxaJurosMensal,
      valorMensalSemJuros * quantidade,
    )

    return roundFloat(calculadoraJuros.calcularPrestacao(quantidade, taxaJurosMensal, saldoMensaisComJuros), 2)
  }

  calcularAjustePorPercentual(
    parcela: TSimulacaoGrupoMensaisBase[number],
    novoPercentual: number,
    valorProposto: number,
    dataSimulacao: string,
    percentualJurosAnualPrice: number,
  ): { valorTotal: number; valorParcela: number } {
    const saldoMensaisSemJuros = new Decimal(novoPercentual).div(100).mul(valorProposto).toNumber()
    const mesesAtePagMensais = differenceInCalendarMonths(parseISO(parcela.dataInicio!), parseISO(dataSimulacao))
    const valorParcela = this.calcularPrestacaoComJuros(
      saldoMensaisSemJuros,
      parcela.quantidade,
      mesesAtePagMensais,
      percentualJurosAnualPrice,
    )

    return { valorTotal: saldoMensaisSemJuros, valorParcela }
  }

  calcularAjustePorDataInicio(
    parcela: TSimulacaoGrupoMensaisBase[number],
    novaDataInicio: string,
    percentual: number,
    valorProposto: number,
    dataSimulacao: string,
    percentualJurosAnualPrice: number,
  ): { valor: number; valorTotal: number; dataInicio: string; dataFim: string; quantidade: number } {
    const novaDataFim = addMonths(parseISO(novaDataInicio), parcela.quantidade - 1)
    const saldoMensaisSemJuros = new Decimal(percentual).div(100).mul(valorProposto).toNumber()
    const mesesAtePagMensais = differenceInCalendarMonths(parseISO(novaDataInicio), parseISO(dataSimulacao))
    const valorParcela = this.calcularPrestacaoComJuros(
      saldoMensaisSemJuros,
      parcela.quantidade,
      mesesAtePagMensais,
      percentualJurosAnualPrice,
    )

    return {
      valor: valorParcela,
      valorTotal: saldoMensaisSemJuros,
      dataInicio: novaDataInicio,
      dataFim: format(novaDataFim, 'yyyy-MM-dd'),
      quantidade: parcela.quantidade,
    }
  }

  calcularAjustePorPrazoDeParcelamento(
    mensais: TSimulacaoGrupoMensalPrice,
    novoPrazo: number,
    dataSimulacao: string,
    percentualJurosAnualPrice: number,
  ): TSimulacaoGrupoMensalPrice {
    const parcela = mensais.parcelas[0]
    if (!parcela?.dataInicio) return mensais

    const novaDataFim = format(addMonths(parseISO(parcela.dataInicio), novoPrazo - 1), 'yyyy-MM-dd')
    const mesesAtePagMensais = differenceInCalendarMonths(parseISO(parcela.dataInicio), parseISO(dataSimulacao))
    const valorParcela = this.calcularPrestacaoComJuros(
      mensais.valorTotal,
      novoPrazo,
      mesesAtePagMensais,
      percentualJurosAnualPrice,
    )

    return {
      ...mensais,
      parcelas: [{ ...parcela, quantidade: novoPrazo, dataFim: novaDataFim, valor: valorParcela }],
    }
  }

  calcularAjustePorValorProposto(
    mensais: TSimulacaoGrupoMensalPrice,
    novoValorProposto: number,
    dataSimulacao: string,
    percentualJurosAnualPrice: number,
  ): TSimulacaoGrupoMensalPrice {
    const parcela = mensais.parcelas[0]
    if (!parcela?.dataInicio) return mensais

    const saldoSemJuros = new Decimal(mensais.percentual).div(100).mul(novoValorProposto).toNumber()
    const mesesAtePagMensais = differenceInCalendarMonths(parseISO(parcela.dataInicio), parseISO(dataSimulacao))
    const valorParcela = this.calcularPrestacaoComJuros(
      saldoSemJuros,
      parcela.quantidade,
      mesesAtePagMensais,
      percentualJurosAnualPrice,
    )

    return {
      percentual: mensais.percentual,
      valorTotal: saldoSemJuros,
      parcelas: [{ ...parcela, valor: valorParcela }],
    }
  }
}
